/* ─────────────────────────────────────────────────────────────────────────────
   PTL Operations → ODA Management — API calls (PTL_ODA_REWORK_DEV_LOG.md).

   Backend: proship_mongo /api/ptl/oda/* (ROLE_PROSHIPADMIN + PTL_ACCESS to view, PTL_SERVICEABILITY_ADMIN to change).
   One ODA source: each courier code's zone map rows (B2BZoneId, cp = prozoId) with ODA type (ODA 1 / 2 / 3, drives the
   courier's ODA charge), km band and days set by hand (both EDD only). EDD days: by hand → ODA type → band → fallback.

     GET   bands                                  → { bands: { bands[{key,label,days}], fallbackDays, categoryDays{ODA1,ODA2,ODA3}, updatedBy, updatedAt }, canEdit }
     PUT   bands  { bands[], fallbackDays, categoryDays, updatedAt } → same (refused when someone saved in between)
     GET   couriers                               → [{ parent, codes[] }]
     GET   pincodes?code&filter(all|type|band|both|hand)&pincode&page&size
                                                  → { rows[{pincode,city,state,zone,odaType,band,bandLabel,handDays,days,source,rule}],
                                                      total, odaPincodes, withType, bandOnly, typeAndBand, daysByHand }
     GET   pincodes/lookup?code&pincode           → { code, pincode, inZoneMap, oda, row }
     POST  pincodes/save?code&pincode&odaType&band&days → row (type or band required; audited per value)
     POST  pincodes/regular?code  [pincodes]      → { counts{madeRegular}, warnings[] } (max 500)
     GET   history?code&pincode&page&size         → [{ at, by, pincode, code, from, to, action(mark|unmark|band|days|upload) }]
     GET   zones/sample?code                      → CSV sample (instruction row + example rows copied from the code’s zone map)
     GET   zones/download?code                    → CSV of the code's whole zone map (upload columns + read-only EDD columns)
     POST  zones/upload?code&confirm&acknowledge (file) → upload check (zone value changes / new pincodes need acknowledge)
     GET   shipments?code&filter(all|type|band|hand|fallback|pastEdd)&q&sort&page&size
                                                  → { rows[...], total, openShipments, odaShipments, withType, withBand, byHand, onFallback, pastEdd, pincodesOnFallback, capped }
   ──────────────────────────────────────────────────────────────────────────── */
import { apiConstants, get, post, put } from '../../common/constants'

/** Server message of a failed call; the same helper as PTL Merchant Setup. */
export { errorText } from '../ptl-merchant-setup/ptlSetupApi'

const BASE = apiConstants.PTL_ODA

const clean = (params) => Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''))
const qs = (params) => {
  const s = new URLSearchParams(clean(params)).toString()
  return s ? `?${s}` : ''
}
const blob = { responseType: 'blob' }

export const fetchOdaBands = () => get(`${BASE}/bands`).then((r) => r.data)
export const saveOdaBands = (bands) => put(`${BASE}/bands`, bands).then((r) => r.data)

export const fetchOdaCouriers = () => get(`${BASE}/couriers`).then((r) => r.data)
export const fetchOdaPincodes = (params) => get(`${BASE}/pincodes`, clean(params)).then((r) => r.data)
export const lookupOdaPincode = (code, pincode) => get(`${BASE}/pincodes/lookup`, { code, pincode }).then((r) => r.data)
export const saveOdaPincode = ({ code, pincode, odaType, band, days }) =>
  post(`${BASE}/pincodes/save${qs({ code, pincode, odaType, band, days })}`).then((r) => r.data)
/** Pincodes back to Regular (max 500): { counts: { madeRegular }, warnings[] }. */
export const makeOdaRegular = (code, pincodes) => post(`${BASE}/pincodes/regular${qs({ code })}`, pincodes).then((r) => r.data)
export const fetchOdaHistory = (params) => get(`${BASE}/history`, clean(params)).then((r) => r.data)
export const fetchOdaShipments = (params) => get(`${BASE}/shipments`, clean(params)).then((r) => r.data)

export const downloadOdaZoneSample = (code) => get(`${BASE}/zones/sample`, { code }, blob)
export const downloadOdaZones = (code) => get(`${BASE}/zones/download`, { code }, blob)

export const uploadOdaZones = ({ code, file, confirm, acknowledge }) => {
  const form = new FormData()
  form.append('file', file)
  return post(`${BASE}/zones/upload${qs({ code, confirm, acknowledge })}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
}
